import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { takeUntil } from 'rxjs/operators';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { UpdateExternalTestLabTestReportRequest } from '../../../../../../../../core/projects.actions';

@Component({
    selector: 'app-external-test-report-details',
    templateUrl: './external-test-report-details.component.html',
    styleUrls: ['./external-test-report-details.component.scss'],
    standalone: false
})
export class ExternalTestReportDetailsComponent implements OnInit, OnDestroy {
  @Input() data: any;
  mediaFiles = [];
  reports: any[] = [];
  labRecommendation = '';
  private recommendationLabels = { passed: 'Pass', failed: 'Fail', n_a: 'N/A' };
  // matches the result pill treatment in offsite-test-card
  private static resultBadges = {
    passed: { label: 'Pass', class: 'result-pass' },
    failed: { label: 'Fail', class: 'result-fail' },
    n_a: { label: 'N/A', class: 'result-na' },
  };
  errorMessages: any = {};
  errorMessageMap = {
    time: { required: 'This field is required.' },
    fromTime: { required: 'This field is required.' },
    fromTimePeriod: { required: 'This field is required.' },
    toTime: { required: 'This field is required.' },
    toTimePeriod: { required: 'This field is required.' },
    break: { required: 'This field is required.' },
    comment: { required: 'This field is required.' },
    pictures: { required: 'This field is required.' },
    videos: { required: 'This field is required.' },
  };
  private resultMap = { 'Pass': 'passed', 'Fail': 'failed', 'N/A': 'n_a' };
  form = new FormGroup<any>({
    comment: new FormControl({ value: null, disabled: false }),
    pictures: new FormControl({ value: JSON.stringify([]), disabled: false }),
    videos: new FormControl({ value: JSON.stringify([]), disabled: false }),
    result: new FormControl('', Validators.required),
  });
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store,
    private commonService: CommonService,
    private projectsService: ProjectsService,
  ) {
  }

  ngOnInit() {
    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = Array.isArray(data) ? data : [data];

        items.forEach((eachItem: any) => {
          if (eachItem?.['fileType'] === 'picture' || eachItem?.['fileType'] === 'document') {
            let pictures = this.form.get('pictures')?.value || '[]';
            pictures = JSON.parse(pictures);
            pictures.push(eachItem);
            this.form?.get('pictures')?.setValue(JSON.stringify(pictures));
            this.mediaFiles.push(eachItem);  // Add to mediaFiles array

          } else if (eachItem?.['fileType'] === 'video') {
            let videos = this.form.get('videos')?.value || '[]';
            videos = JSON.parse(videos);

            videos.push(eachItem);
            this.form?.get('videos')?.setValue(JSON.stringify(videos));
            this.mediaFiles.push(eachItem);  // Add to mediaFiles array
          }
        });

        this.mediaFiles = [...this.mediaFiles];
      });

    this.projectsService.changeView
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.data = {};
        this.mediaFiles = [];
        this.reports = [];
        if (data?.id) {
          this.data = data;
          this.labRecommendation = this.recommendationLabels[this.data?.lab_recommendation] || '';

          if (this.data?.material_test_report?.attachments?.length) {
            this.mediaFiles = this.mapFileTypes(this.data.material_test_report.attachments);
          }
          this.prepareReports();
        }
      });

    if (this.data?.material_test_report?.attachments?.length) {
      this.mediaFiles = this.mapFileTypes(this.data.material_test_report.attachments);
    }
    this.labRecommendation = this.recommendationLabels[this.data?.lab_recommendation] || '';
    this.prepareReports();
  }

  /**
   * All uploaded reports (oldest -> newest, matching the internal smt-summary
   * list); falls back to the singular material_test_report for stale payloads
   */
  private prepareReports() {
    const list = this.data?.material_test_reports?.length
      ? this.data.material_test_reports
      : (this.data?.material_test_report?.id ? [this.data.material_test_report] : []);
    this.reports = list.map(r => ({
      ...r,
      mediaFiles: this.mapFileTypes(r.attachments || []),
    }));
  }

  /**
   * Badge label/class for a report result; null result -> no badge
   * @param result 'passed' | 'failed' | 'n_a' | null
   */
  resultBadge(result: string): { label: string; class: string } | null {
    return ExternalTestReportDetailsComponent.resultBadges[result] || null;
  }

  save() {
    const formValues = this.form.getRawValue();
    const tempPictures = JSON.parse(formValues?.pictures)?.map(o => o.id);
    const tempVideos = JSON.parse(formValues?.videos)?.map(o => o.id);
    const payload = {
      comment: formValues?.comment,
      attachments: [...tempPictures, ...tempVideos],
      result: this.resultMap[formValues?.result] || null,
      testId: this.data.id,
    };
    this.store.dispatch(UpdateExternalTestLabTestReportRequest({ payload }));
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  deletePicture(pic) {
    if (pic.fileType === 'picture' || pic.fileType === 'document') {
      let pictures = this.getPictures();

      pictures = pictures?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('pictures')?.setValue(JSON.stringify(pictures));
    }

    if (pic.fileType === 'video') {
      let videos = this.getVideos();

      videos = videos?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('videos')?.setValue(JSON.stringify(videos));
    }
  }

  getPictures() {
    const pictures = this.form.controls['pictures']?.value || '[]';
    return JSON.parse(pictures);
  }

  getVideos() {
    const videos = this.form.controls['videos']?.value || '[]';
    return JSON.parse(videos);
  }

  private mapFileTypes(attachments: any[]): any[] {
    return JSON.parse(JSON.stringify(attachments)).map(item => {
      if (!item.fileType) {
        if (item.mime?.startsWith('image/')) {
          item.fileType = 'picture';
        } else if (item.mime?.startsWith('video/')) {
          item.fileType = 'video';
        } else if (item.mime?.startsWith('application/') || item.mime?.startsWith('text/')) {
          item.fileType = 'document';
        }
      }
      return item;
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
